import { css, html } from "@web/helpers/webComponents";
import "../screens/GameOverQuoteScreen";
import { IPuzzleComponent } from "../PuzzleComponent";

const styles: string = css`
    :host {
        display: block;
        padding: 10px;
    }
    
    h1 {
        position: absolute;
        top: 1rem;
        left: 50%;
        transform: translateX(-50%);
        font-size: 2em;
        color: var(--color-white);
    }
    
    p {
        position: absolute;
        top: 5rem;
        left: 50%;
        transform: translateX(-50%);
        font-size: 1.2em;
        color: var(--color-white);
        text-align: center;
    }
    
    /* stage 1 */
    
    /* bomb container and screws */

    .bomb-container {
        position: relative;
        width: 50vw;
        margin: 0 auto;
    }

    .bomb-container img {
        width: 100%;
        display: block;
    }

    .bomb-container .screw {
        position: absolute;
        width: 4%;
        height: 4%;
        background-color: var(--color-gray);
        list-style: none;
        opacity: 0;
        cursor: pointer;
    }

    .screw-1 {
        top: 27%;
        left: 40%;
    }

    .screw-2 {
        top: 27%;
        right: 25%;
    }

    .screw-3 {
        top: 62%;
        left: 40%;
    }

    .screw-4 {
        top: 62%;
        right: 25%;
    }
    
    /* stage 2 */
    
    .codelock-container {
        position: relative;
        width: 50vw;
        margin: 0 auto;
    }
    
    .codelock-container img {
        width: 100%;
        margin-bottom: 1.5rem;
        display: block;
    }
    
    .codelock-input {
        position: absolute;
        top: 29%;
        left: 57%;
        text-align: center;
        transform: translateX(-50%);
        font-size: 3.4vw;
        color: var(--color-white);
        
        text-decoration: underline;
        text-underline-offset: 0.2em;
    }

    .key {
        position: absolute;
        width: 4%;
        height: 4%;
        background-color: var(--color-gray);
        list-style: none;
        opacity: 0;
        cursor: pointer;
    }

    /* row 1 */
    
    .key-1 {
        top: 52.5%;
        left: 43%;
    }

    .key-2 {
        top: 52.5%;
        left: 49%;
    }

    .key-3 {
        top: 52.5%;
        left: 54.5%;
    }

    .key-4 {
        top: 52.5%;
        left: 60%;
    }

    .key-5 {
        top: 52.5%;
        left: 65.5%;
    }
    
    /* row 2 */
    
    .key-6 {
        top: 59%;
        left: 43%;
    }

    .key-7 {
        top: 59%;
        left: 49%;
    }

    .key-8 {
        top: 59%;
        left: 54.5%;
    }

    .key-9 {
        top: 59%;
        left: 60%;
    }

    .key-10 {
        top: 59%;
        left: 65.5%;
    }
    
    /* stage 3 */

    .cutwire-container {
        position: relative;
        width: 50vw;
        margin: 0 auto;
    }

    .cutwire-container img {
        width: 100%;
        margin-bottom: 1.5rem;
        display: block;
    }
    
    .wire {
        position: absolute;
        width: 4%;
        height: 4%;
        list-style: none;
        opacity: 1;
        cursor: pointer;
    }
    
    .red-1 {
        top: 34%;
        left: 82%;
        background-color: red;
    }
    
    .red-2 {
        top: 70%;
        left: 60%;
        background-color: red;
    }
    
    .yellow-1 {
        top: 48%;
        left: 82%;
        background-color: yellow;
    }
    
    .yellow-2 {
        top: 70%;
        left: 52%;
        background-color: yellow;
    }
    
    .black-1 {
        top: 64%;
        left: 82%;
        background-color: black;
    }
    
    .black-2 {
        top: 70%;
        left: 43%;
        background-color: black;
    }

    /* cursors */

    .cursor-wrong {
        cursor: url("/assets/img/puzzles/10/wrong.png") 16 16, not-allowed;
    }
    
    /* stages */
    
    .stage.hidden {
        display: none;
    }
    
    .stage.active {
        display: block;
    }

    .stage-2 {
        display: none;
    }
    
    .stage-3 {
        display: none;
    }
    
    /* red flash */
    .red-flash {
        position: fixed;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(255, 0, 0, 0.4);
        pointer-events: none;
        opacity: 0;
        z-index: 25;
    }

    .red-flash.active {
        animation: flash 0.6s ease-out;
    }

    @keyframes flash {
        0% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }
    
    /* mobile responsiveness */
    
    @media (max-width: 600px) {
        .bomb-container {
            width: 90vw;
        }
        
        .codelock-container {
            width: 90vw;
        }
    }
    
`;

export class BombDefusePuzzle extends HTMLElement implements IPuzzleComponent {
    /** Audio element for playing action sounds */
    private _actionAudio?: HTMLAudioElement;
    /** Duration for which the wrong cursor is shown, in ms */
    private readonly wrongCursorDuration: number = 1500;
    /** Number of screws remaining */
    private _remainingScrews: number = 4;
    /** Entered code string for stage 2 */
    private _enteredCode: string = "";
    /** Correct code for stage 2 */
    private readonly _correctCode: string = "남궁용길";

    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
    }

    /**
     * Shows wrong cursor icon
     *
     * @param element
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private showWrongCursor(element: HTMLElement): void {
        element.classList.add("cursor-wrong");
        setTimeout(() => {
            element.classList.remove("cursor-wrong");
        }, this.wrongCursorDuration);
    }

    /**
     * Plays the sound and shows the wrong cursor when the bomb is clicked
     *
     * @param bomb
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private onBombClicked(bomb: HTMLElement): void {
        this.playSound("/assets/sound/puzzles/10/hit_heavy_05.wav");
        this.showWrongCursor(bomb);
    }

    /**
     * Plays the sound and removes the screw when it is clicked, then checks if all screws are removed to go to the next stage
     *
     * @param screw
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private onScrewClicked(screw: HTMLElement): void {
        this.playSound("/assets/sound/puzzles/10/electronics_force_shutdown.wav");

        screw.remove();
        this._remainingScrews--;

        if (this._remainingScrews === 0) {
            this.goToStage(2);
        }
    }

    /**
     * Plays 1 of the 4 sounds and adds the character to the string when a key is clicked
     *
     * @param key
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private onKeyClicked(key: HTMLElement): void {
        const soundNumber: number = Math.floor(Math.random() * 9) + 1;
        this.playSound(`/assets/sound/puzzles/10/UI_click_0${soundNumber}.wav`);

        const character: string | null = key.title;

        if (character) {
            this._enteredCode += character;
            this.updateCodelockDisplay();
        }
    }

    /**
     * Cuts the wire and changes the bomb image to the one without it
     *
     * @param wire
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private onWireClicked(wire: HTMLElement): void {
        if (!this.shadowRoot) {
            return;
        }

        const bombImage: HTMLImageElement | null = this.shadowRoot.querySelector("#bombWires");

        if (bombImage) {
            switch (wire.id) {
                case "red1":
                case "red2":
                    bombImage.src = "/assets/img/puzzles/10/codelock_no_red_wire.png";
                    this.playSound("/assets/sound/puzzles/10/cut_good.wav");

                    this.dispatchEvent(
                        new Event("puzzle-complete", { bubbles: true, composed: true })
                    );

                    break;
                case "yellow1":
                case "yellow2":
                    bombImage.src = "/assets/img/puzzles/10/codelock_no_yellow_wire.png";
                    this.playSound("/assets/sound/puzzles/10/cut.wav");
                    this.showEnding();
                    break;
                case "black1":
                case "black2":
                    bombImage.src = "/assets/img/puzzles/10/codelock_no_black_wire.png";
                    this.playSound("/assets/sound/puzzles/10/cut.wav");
                    this.showEnding();
                    break;
            }
        }
    }

    /**
     * Shows ending dialog
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private showEnding(): void {
        if (!this.shadowRoot) {
            return;
        }

        const dialog: HTMLDialogElement | null | undefined = this.shadowRoot.querySelector("game-over-quote-screen")?.shadowRoot?.querySelector("#ending");

        if (dialog) {
            dialog.showModal();
        }
    }

    /**
     * Updates the displayed code on the codelock screen
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private updateCodelockDisplay(): void {
        if (!this.shadowRoot) {
            return;
        }

        const input: HTMLElement | null = this.shadowRoot.querySelector("#codelockInput");

        // if there are already 4 characters, validate the code instead of adding more characters
        if (this._enteredCode.length >= 4) {
            this.validateCode();

            return;
        }

        if (input) {
            input.textContent = this._enteredCode;
        }
    }

    /**
     * Validate code and complete the puzzle if the code is correct, otherwise play wrong sound and make screen go red for a moment
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private validateCode(): void {
        if (this._enteredCode === this._correctCode) {
            this.goToStage(3);
        }
        else {
            if (!this.shadowRoot) {
                return;
            }

            const input: HTMLElement | null = this.shadowRoot.querySelector("#codelockInput");

            if (input) {
                this.showWrongCursor(input);
                this.playSound("/assets/sound/puzzles/10/negative_16.wav");

                this.flashRedScreen();

                this._enteredCode = "";
                this.updateCodelockDisplay();
            }
        }
    }

    /**
     * Plays the sound from the passed link
     *
     * @param src
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private playSound(src: string): void {
        this._actionAudio = new Audio(src);
        this._actionAudio.play().catch((error: unknown): void => {
            console.log(error);
        });
    }

    /**
     * Flashes the screen red for a moment to indicate a wrong action
     *
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private flashRedScreen(): void {
        if (!this.shadowRoot) {
            return;
        }

        const flash: HTMLElement | null = this.shadowRoot.querySelector("#redFlash");

        if (flash) {
            flash.classList.remove("active");

            // trigger rerender to restart the animation
            void flash.offsetWidth;

            flash.classList.add("active");
        }
    }

    /**
     * Shows the stage with the passed number and hides the others
     *
     * @param stage
     * @private
     *
     * @author Kyrylo Maliuha
     */
    private goToStage(stage: number): void {
        if (!this.shadowRoot) {
            return;
        }

        this.shadowRoot.querySelectorAll("section").forEach(
            (section: HTMLElement) => {
                section.classList.add("hidden");
                section.classList.remove("active");
            }
        );

        this.shadowRoot.querySelector(`.stage-${stage}`)?.classList.add("active");
        this.shadowRoot.querySelector(`.stage-${stage}`)?.classList.remove("hidden");
    }

    /**
     * @interitdoc
     */
    public start(): void {
        if (!this.shadowRoot) {
            return;
        }

        const puzzle: HTMLElement = html`
            <div>
                <div class="red-flash" id="redFlash"></div>
                <style>${styles}</style>
                
                <!-- stage 1 -->
                <section class="stage stage-1">
                    <h1>STEP 1</h1>
                    <p>Unscrew the plate</p>
                    <div class="bomb-container">
                        <img draggable="false" id="bomb" src="/assets/img/puzzles/10/bomb.png" alt="bomb" />
                        
                        <!-- screws -->
                        <ul>
                            <li id="screw1" class="screw screw-1"></li>
                            <li id="screw2" class="screw screw-2"></li>
                            <li id="screw3" class="screw screw-3"></li>
                            <li id="screw4" class="screw screw-4"></li>
                        </ul>
                    </div>
                </section>
                
                <!-- stage 2 -->
                <section class="stage stage-2">
                    <h1>STEP 2</h1>
                    <p>Enter the code</p>
                    <div class="codelock-container">
                        <h2 id="codelockInput" class="codelock-input">${this._enteredCode}</h2>
                        <img draggable="false" id="bombCodelock" src="/assets/img/puzzles/10/codelock.png" alt="bomb without front panel" />

                        <!-- keys -->
                        <ul>
                            <!-- row 1 -->
                            <li id="key1" class="key key-1" title="궁"></li>
                            <li id="key2" class="key key-2" title="김"></li>
                            <li id="key3" class="key key-3" title="남"></li>
                            <li id="key4" class="key key-4" title="철"></li>
                            <li id="key5" class="key key-5" title="용"></li>
                            
                            <!-- row 2 -->
                            <li id="key6" class="key key-6" title="한"></li>
                            <li id="key7" class="key key-7" title="길"></li>
                            <li id="key8" class="key key-8" title="박"></li>
                            <li id="key9" class="key key-9" title="민"></li>
                            <li id="key10" class="key key-10" title="정"></li>
                        </ul>
                    </div>
                </section>

                <!-- stage 3 -->
                <section class="stage stage-3">
                    <h1>STEP 3</h1>
                    <p>Cut the wire</p>
                    <div class="cutwire-container">
                        <img draggable="false" id="bombWires" src="/assets/img/puzzles/10/codelock.png" alt="bomb with an errored screen and unscrewed front panel" />

                        <!-- wires points -->
                        <ul>
                            <!-- side -->
                            <li id="yellow1" class="wire yellow-1"></li>
                            <li id="black1" class="wire black-1"></li>
                            <li id="red1" class="wire red-1"></li>

                            <!-- bottom -->
                            <li id="yellow2" class="wire yellow-2"></li>
                            <li id="black2" class="wire black-2"></li>
                            <li id="red2" class="wire red-2"></li>
                        </ul>
                    </div>
                </section>
                
                <game-over-quote-screen></game-over-quote-screen>
            </div>
        `;

        this.shadowRoot.replaceChildren(puzzle);

        // play starting sound
        this.playSound("/assets/sound/puzzles/10/rumble_02.wav");

        // wrong action sound and cursor
        const bomb: HTMLElement | null = this.shadowRoot.getElementById("bomb");

        bomb?.addEventListener("click", () => {
            this.onBombClicked(bomb);
        });

        // add a click listener to all screws on stage 1
        const screws: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll(".screw");

        screws.forEach((screw: HTMLElement) => {
            screw.addEventListener("click", () => {
                this.onScrewClicked(screw);
            });
        });

        // add a click listener to all codelock keys on stage 2
        const keys: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll(".key");

        keys.forEach((key: HTMLElement) => {
            key.addEventListener("click", () => {
                this.onKeyClicked(key);
            });
        });

        // add a click listener to all wires on stage 3
        const wires: NodeListOf<HTMLElement> = this.shadowRoot.querySelectorAll(".wire");

        wires.forEach((wire: HTMLElement) => {
            wire.addEventListener("click", () => {
                this.onWireClicked(wire);
            });
        });
    }
}

// "10" is the alias of the room where the puzzle is located
customElements.define("puzzle-10", BombDefusePuzzle);
